import * as React from "react";
import { useState, useEffect } from "react";
import { api } from "../api";
import { RLink } from "../DesignSystem/RLink";
import { Page } from "../DesignSystem/formatting/TwoColumnView";
import { FeedPanel } from "../DesignSystem/formatting/FeedComponents";
import { ChipsAPI } from "../../../src/apidefs";
import { AcademicCapIcon } from "@heroicons/react/24/solid";

type IntelligentResponse = ChipsAPI["/api/most-intelligent"]["GET"]["response"];

const getGradeColor = (grade: number): string => {
  if (grade >= 16) return "text-violet-500";
  if (grade >= 12) return "text-blue-500";
  if (grade >= 8) return "text-emerald-500";
  if (grade >= 5) return "text-amber-500";
  return "text-slate-500";
};

export function MostIntelligentPage() {
  const [data, setData] = useState<IntelligentResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      const result = await api.get<"/api/most-intelligent">(
        "/api/most-intelligent"
      );
      if (!api.isToast(result)) {
        setData(result);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  return (
    <Page>
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold text-slate-900 dark:text-white">
          The Most Intelligent People on Chips
        </h1>
      </div>

      <div className="mx-auto max-w-3xl">
        <FeedPanel
          title={
            <span className="flex items-center gap-2">
              <AcademicCapIcon className="h-6 w-6 text-violet-500" />
              Intelligence Leaderboard
            </span>
          }
        >
          {loading ? (
            <div className="py-8 text-center text-slate-500">
              Calculating brain sizes...
            </div>
          ) : data?.users.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              No one has written enough to be ranked yet!
            </div>
          ) : (
            <div className="space-y-2">
              {data?.users.map((user, index) => (
                <div
                  key={user.userId}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 transition ${
                    index === 0
                      ? "bg-gradient-to-r from-violet-100 to-purple-100 dark:from-violet-900/30 dark:to-purple-900/30"
                      : index === 1
                        ? "bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20"
                        : index === 2
                          ? "bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20"
                          : "hover:bg-slate-50 dark:hover:bg-slate-700/50"
                  }`}
                >
                  <span className="w-8 text-center text-sm font-bold text-slate-400">
                    #{index + 1}
                  </span>
                  <img
                    src={user.avatarUrl}
                    alt={user.username}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <RLink
                      to={`/user/${user.username}`}
                      className="block truncate font-semibold hover:underline"
                    >
                      {user.username}
                    </RLink>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {user.totalComments} comments, {user.totalReviews} reviews
                      ({user.totalWords.toLocaleString()} words)
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className={`text-lg font-bold ${getGradeColor(user.fleschKincaid)}`}
                    >
                      {user.fleschKincaid.toFixed(1)}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {user.gradeDescription}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </FeedPanel>
      </div>
    </Page>
  );
}
